import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ReactMarkdown from "react-markdown";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, History, Plus, Trash2, ArrowLeft, MapPin, User as UserIcon, Brain,
  CheckCircle2, XCircle, ArrowRight, Zap, Newspaper, Lightbulb, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mdComponents = {
  p: (props) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  ul: (props) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
  ol: (props) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
  h1: (props) => <p className="font-bold text-base mt-2 mb-1" {...props} />,
  h2: (props) => <p className="font-bold text-sm mt-2 mb-1" {...props} />,
  h3: (props) => <p className="font-semibold text-sm mt-2 mb-1" {...props} />,
  code: (props) => <code className="bg-black/10 dark:bg-white/10 rounded px-1 text-xs" {...props} />,
  a: (props) => <a className="text-emerald-600 underline" target="_blank" rel="noreferrer" {...props} />,
};

export function InnovationCopilot() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | history
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const authHeaders = useCallback(
    () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  // Prompts contextuales según la página actual
  useEffect(() => {
    if (!open || !token) return;
    fetch(`${API}/assistant/prompts?path=${encodeURIComponent(location.pathname)}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setPrompts(d.prompts || []))
      .catch(() => {});
  }, [open, location.pathname, token, authHeaders]);

  const loadSessions = async () => {
    try {
      const r = await fetch(`${API}/assistant/sessions`, { headers: authHeaders() });
      const d = await r.json();
      setSessions(d.sessions || []);
    } catch {
      toast.error("No se pudieron cargar las conversaciones");
    }
  };

  const openSession = async (sid) => {
    try {
      const r = await fetch(`${API}/assistant/sessions/${sid}/messages`, { headers: authHeaders() });
      const d = await r.json();
      setSessionId(sid);
      setMessages((d.messages || []).map((m) => ({ role: m.role, content: m.content, actions: m.actions || [] })));
      setView("chat");
    } catch {
      toast.error("No se pudo abrir la conversación");
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    await fetch(`${API}/assistant/sessions/${sid}`, { method: "DELETE", headers: authHeaders() });
    setSessions((prev) => prev.filter((s) => s.id !== sid));
    if (sid === sessionId) newConversation();
  };

  const [memory, setMemory] = useState([]);
  const [teamMemory, setTeamMemory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [expandedSug, setExpandedSug] = useState(null);
  const isAdmin = user?.role === "admin";
  const loadMemory = async () => {
    try {
      const reqs = [
        fetch(`${API}/assistant/memory`, { headers: authHeaders() }),
        fetch(`${API}/assistant/team-memory`, { headers: authHeaders() }),
      ];
      if (user?.role === "admin") {
        reqs.push(fetch(`${API}/assistant/team-memory/suggestions`, { headers: authHeaders() }));
      }
      const results = await Promise.all(reqs);
      setMemory(await results[0].json());
      setTeamMemory(await results[1].json());
      if (results[2]) {
        const sR = results[2];
        setSuggestions(sR.ok ? await sR.json() : []);
      }
    } catch { toast.error("Error cargando memoria"); }
  };
  const deleteMemoryItem = async (mid) => {
    await fetch(`${API}/assistant/memory/${mid}`, { method: "DELETE", headers: authHeaders() });
    setMemory((prev) => prev.filter((m) => m.id !== mid));
  };
  const addMemoryItem = async (text) => {
    const r = await fetch(`${API}/assistant/memory`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ content: text, category: "preferencia" }),
    });
    const created = await r.json();
    setMemory((prev) => [created, ...prev]);
  };
  const addTeamRule = async (text) => {
    const r = await fetch(`${API}/assistant/team-memory`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ content: text, category: "regla" }),
    });
    if (!r.ok) { toast.error("Solo admins pueden añadir reglas"); return; }
    const created = await r.json();
    setTeamMemory((prev) => [created, ...prev]);
  };
  const deleteTeamRule = async (tid) => {
    const r = await fetch(`${API}/assistant/team-memory/${tid}`, { method: "DELETE", headers: authHeaders() });
    if (!r.ok) { toast.error("Solo admins"); return; }
    setTeamMemory((prev) => prev.filter((m) => m.id !== tid));
  };
  const runSuggestionScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const r = await fetch(`${API}/assistant/team-memory/scan`, { method: "POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Error escaneando"); return; }
      if (d.ok && d.created > 0) toast.success(`${d.created} sugerencia(s) nueva(s)`);
      else if (d.ok) toast.info("Sin nuevos patrones detectados");
      else toast.error(d.reason || "No se pudo escanear");
      // Refresh list
      const sR = await fetch(`${API}/assistant/team-memory/suggestions`, { headers: authHeaders() });
      setSuggestions(sR.ok ? await sR.json() : []);
    } catch { toast.error("Error escaneando"); }
    finally { setScanning(false); }
  };
  const acceptSuggestion = async (sid) => {
    const r = await fetch(`${API}/assistant/team-memory/suggestions/${sid}/accept`, { method: "POST", headers: authHeaders() });
    if (!r.ok) { toast.error("No se pudo aceptar"); return; }
    const d = await r.json();
    setSuggestions((prev) => prev.filter((s) => s.id !== sid));
    if (d.rule) setTeamMemory((prev) => [d.rule, ...prev]);
    toast.success("Regla añadida al equipo");
  };
  const dismissSuggestion = async (sid) => {
    const r = await fetch(`${API}/assistant/team-memory/suggestions/${sid}/dismiss`, { method: "POST", headers: authHeaders() });
    if (!r.ok) { toast.error("No se pudo descartar"); return; }
    setSuggestions((prev) => prev.filter((s) => s.id !== sid));
  };

  const newConversation = () => {
    setSessionId(null);
    setMessages([]);
    setView("chat");
  };

  const loadBriefing = async () => {
    if (loading) return;
    setView("chat");
    setMessages((prev) => [...prev, { role: "user", content: "📰 Genera el resumen semanal de innovación" }]);
    setLoading(true);
    try {
      const r = await fetch(`${API}/assistant/briefing/preview`, { headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      setMessages((prev) => [...prev, { role: "assistant", content: d.briefing, briefing: true, teamsConfigured: d.teams_configured }]);
    } catch (e) {
      toast.error(`No se pudo generar el resumen: ${e.message}`);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const sendBriefingToTeams = async (briefingText, idx) => {
    try {
      const r = await fetch(`${API}/assistant/briefing/send`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ briefing: briefingText }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      toast.success(`📨 Publicado en Teams${d.emails_sent ? ` y enviado por email a ${d.emails_sent} personas` : ""}`);
      setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, briefingSent: true } : m)));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const r = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ session_id: sessionId, message: text, page_path: location.pathname }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      setSessionId(d.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: d.response, actions: d.actions || [] }]);
      if ((d.actions || []).some((a) => a.ok)) {
        toast.success("⚡ VAL ha ejecutado la acción");
      }
    } catch (err) {
      toast.error(`VAL no pudo responder: ${err.message}`);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-2xl hover:scale-105 transition-all z-50 border-0"
          style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}
          data-testid="copilot-trigger"
          aria-label="Abrir asistente VAL"
          title="Asistente VAL"
        >
          <Sparkles className="w-6 h-6 text-white" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:w-[480px] sm:max-w-[480px] flex flex-col p-0 gap-0">
        <SheetTitle className="sr-only">VAL · Copiloto de Innovación</SheetTitle>
        <SheetDescription className="sr-only">Asistente de IA con acceso a los datos de la plataforma</SheetDescription>
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center gap-3" style={{ background: "linear-gradient(135deg, #007934, #0a5c33)" }}>
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center text-white font-black text-base flex-shrink-0">
            VAL
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm leading-tight">VAL · Copiloto de Innovación</p>
            <p className="text-white/70 text-[11px] flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              Contexto: {location.pathname === "/" ? "Inicio" : location.pathname}
            </p>
          </div>
          <Button
            variant="ghost" size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
            onClick={loadBriefing}
            title="Resumen semanal (se publica en Teams cada lunes)"
            data-testid="copilot-briefing-btn"
          >
            <Newspaper className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
            onClick={newConversation}
            title="Nueva conversación"
            data-testid="copilot-new-chat"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
            onClick={() => { if (view === "memory") { setView("chat"); } else { loadMemory(); setView("memory"); } }}
            title="Memoria de VAL"
            data-testid="copilot-memory-btn"
          >
            {view === "memory" ? <ArrowLeft className="w-4 h-4" /> : <Brain className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost" size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
            onClick={() => { if (view === "history") { setView("chat"); } else { loadSessions(); setView("history"); } }}
            title="Historial"
            data-testid="copilot-history-btn"
          >
            {view === "history" ? <ArrowLeft className="w-4 h-4" /> : <History className="w-4 h-4" />}
          </Button>
        </div>

        {view === "memory" ? (
          <ScrollArea className="flex-1 p-4">
            {/* Suggestions (admin only): patterns detected across users */}
            {isAdmin && (
              <div className="mb-5 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/70 to-fuchsia-50/50 p-3" data-testid="team-suggestions-block">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-violet-600" />
                  <p className="text-xs font-semibold text-violet-900 uppercase tracking-wider">Sugerencias de VAL</p>
                  <button
                    onClick={runSuggestionScan}
                    disabled={scanning}
                    className="ml-auto text-[11px] px-2 py-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white font-medium disabled:opacity-50 flex items-center gap-1"
                    title="Escanear memorias individuales y proponer reglas de equipo"
                    data-testid="team-suggestions-scan-btn"
                  >
                    {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {scanning ? "Analizando…" : "Escanear"}
                  </button>
                </div>
                <p className="text-xs text-violet-800/80 mb-2">Patrones detectados en la memoria del equipo. Convierte los que quieras en reglas oficiales.</p>
                {suggestions.length === 0 ? (
                  <p className="text-xs text-violet-700/70 mt-2 italic">Sin sugerencias pendientes. Pulsa &quot;Escanear&quot; para analizar patrones.</p>
                ) : (
                  <div className="space-y-2 mt-2" data-testid="team-suggestions-list">
                    {suggestions.map((s) => {
                      const isOpen = expandedSug === s.id;
                      return (
                        <div key={s.id} className="rounded-lg bg-white border border-violet-200 overflow-hidden">
                          <div className="p-2.5">
                            <div className="flex items-start gap-2">
                              <span className="text-[10px] uppercase tracking-wider text-violet-800 bg-violet-100 px-1.5 py-0.5 rounded font-semibold shrink-0 mt-0.5">
                                {s.category}
                              </span>
                              <p className="text-sm flex-1 leading-relaxed font-medium">{s.content}</p>
                              <span className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded shrink-0 mt-0.5" title="Personas que soportan el patrón">
                                {s.support_count}👥
                              </span>
                            </div>
                            {s.rationale && (
                              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed pl-1">{s.rationale}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => acceptSuggestion(s.id)}
                                className="text-[11px] px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center gap-1"
                                data-testid={`team-suggestion-accept-${s.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3" /> Aceptar
                              </button>
                              <button
                                onClick={() => dismissSuggestion(s.id)}
                                className="text-[11px] px-2 py-1 rounded-md bg-white border hover:bg-muted text-foreground font-medium flex items-center gap-1"
                                data-testid={`team-suggestion-dismiss-${s.id}`}
                              >
                                <XCircle className="w-3 h-3" /> Descartar
                              </button>
                              {s.evidence?.length > 0 && (
                                <button
                                  onClick={() => setExpandedSug(isOpen ? null : s.id)}
                                  className="text-[11px] px-2 py-1 rounded-md hover:bg-muted text-muted-foreground font-medium flex items-center gap-1 ml-auto"
                                  data-testid={`team-suggestion-evidence-${s.id}`}
                                >
                                  {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  Evidencia ({s.evidence.length})
                                </button>
                              )}
                            </div>
                          </div>
                          {isOpen && s.evidence?.length > 0 && (
                            <div className="bg-violet-50/60 border-t border-violet-200 p-2.5 space-y-1.5">
                              {s.evidence.map((e, i) => (
                                <div key={i} className="text-xs">
                                  <span className="font-semibold text-violet-900">{e.user_name}:</span>{" "}
                                  <span className="text-muted-foreground italic">&quot;{e.content}&quot;</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Team rules (admin managed, applies to everyone) */}
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-600">👥</span>
                <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Reglas del equipo</p>
                {!isAdmin && <span className="text-[10px] text-amber-700 ml-auto">solo lectura</span>}
              </div>
              <p className="text-xs text-amber-800/80 mb-3">Aplicadas a todo el equipo. Prevalecen sobre la memoria individual.</p>
              {isAdmin && <MemoryAdder onAdd={addTeamRule} placeholder='Ej: "Priorizar CDTI sobre EU si cierre < 30 días"' testIdPrefix="team-memory" />}
              {teamMemory.length === 0 ? (
                <p className="text-xs text-amber-700/80 mt-2">Sin reglas de equipo.{isAdmin ? "" : " Un admin puede añadirlas."}</p>
              ) : (
                <div className="space-y-2 mt-2" data-testid="team-memory-list">
                  {teamMemory.map((m) => (
                    <div key={m.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-white border border-amber-200">
                      <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-semibold shrink-0 mt-0.5">
                        {m.category}
                      </span>
                      <p className="text-sm flex-1 leading-relaxed">{m.content}</p>
                      {isAdmin && (
                        <button
                          onClick={() => deleteTeamRule(m.id)}
                          className="text-muted-foreground hover:text-red-500 transition-colors"
                          title="Eliminar regla"
                          data-testid={`team-memory-delete-${m.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Individual memory */}
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-emerald-600" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tu memoria personal</p>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              VAL recuerda tus preferencias entre sesiones. Añade o elimina lo que necesites.
            </p>
            <MemoryAdder onAdd={addMemoryItem} />
            {memory.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-4">Sin memoria aún. Cuéntame algo como &quot;prefiero informes cortos y con bullets&quot; y lo guardaré.</p>
            ) : (
              <div className="space-y-2 mt-3" data-testid="memory-list">
                {memory.map((m) => (
                  <div key={m.id} className="flex items-start gap-2 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded font-semibold shrink-0 mt-0.5">
                      {m.category}
                    </span>
                    <p className="text-sm flex-1 leading-relaxed">{m.content}</p>
                    <button
                      onClick={() => deleteMemoryItem(m.id)}
                      className="text-muted-foreground hover:text-red-500 transition-colors"
                      title="Olvidar"
                      data-testid={`memory-delete-${m.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        ) : view === "history" ? (
          <ScrollArea className="flex-1 p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Conversaciones guardadas</p>
            {sessions.length === 0 && (
              <p className="text-sm text-muted-foreground">Aún no hay conversaciones.</p>
            )}
            <div className="space-y-2">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className="w-full text-left p-3 rounded-lg border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors group"
                  data-testid={`copilot-session-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.title}</p>
                      {s.last_message && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{s.last_message}</p>
                      )}
                    </div>
                    <Trash2
                      className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive flex-shrink-0 mt-0.5"
                      onClick={(e) => deleteSession(s.id, e)}
                    />
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <>
            <ScrollArea className="flex-1 px-4 py-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3"
                      style={{ background: "linear-gradient(135deg, #00793420, #3BAD2D30)" }}>
                      <Sparkles className="w-7 h-7 text-emerald-600" />
                    </div>
                    <p className="font-semibold text-sm">¿En qué te ayudo?</p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-[280px] mx-auto">
                      Conozco tus retos, pilotos y portfolio. Pregunta, pide resúmenes o dime que <strong>cree retos, lance investigaciones o valore iniciativas</strong> por ti ⚡
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Sugerencias para esta página</p>
                    {prompts.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        className="w-full text-left text-xs p-2.5 rounded-lg border hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors"
                        data-testid={`copilot-prompt-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[9px] font-black"
                        style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}>
                        VAL
                      </div>
                    )}
                    <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}>
                      {msg.role === "assistant" ? (
                        <>
                          <ReactMarkdown components={mdComponents}>{msg.content}</ReactMarkdown>
                          {msg.briefing && (
                            <div className="mt-2.5 flex items-center gap-2">
                              {msg.briefingSent ? (
                                <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Publicado en Teams + Email
                                </span>
                              ) : msg.teamsConfigured ? (
                                <Button
                                  size="sm"
                                  className="h-7 text-xs"
                                  style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}
                                  onClick={() => sendBriefingToTeams(msg.content, i)}
                                  data-testid="copilot-briefing-send"
                                >
                                  <Send className="w-3 h-3 mr-1.5" /> Publicar en Teams + Email
                                </Button>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">Webhook de Teams no configurado</span>
                              )}
                              <span className="text-[10px] text-muted-foreground">· se envía automáticamente cada lunes 9:00</span>
                            </div>
                          )}
                          {(msg.actions || []).map((a, ai) => (
                            <div
                              key={ai}
                              className={`mt-2 p-2.5 rounded-lg border flex items-start gap-2 ${
                                a.ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"
                              }`}
                              data-testid={`copilot-action-${ai}`}
                            >
                              {a.ok ? (
                                <Zap className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold leading-snug">{a.label}</p>
                                {a.ok && a.kind === "budget_deviation" && (
                                  <div className="mt-2 space-y-1.5">
                                    <div className="flex flex-wrap gap-2 text-[11px]">
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">Plan {(a.plan_total_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">Real {(a.real_total_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                      <span className={`px-1.5 py-0.5 rounded font-mono font-bold ${Math.abs(a.total_dev_pct) >= 30 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                                        Δ {a.total_dev_pct > 0 ? "+" : ""}{a.total_dev_pct}%
                                      </span>
                                    </div>
                                    {a.commentary_ai && (
                                      <div className="text-[11px] leading-relaxed p-2 bg-white/70 rounded border-l-2 border-emerald-500">
                                        {a.commentary_ai}
                                      </div>
                                    )}
                                    {a.offenders?.length > 0 && (
                                      <div className="text-[11px] space-y-0.5 mt-1">
                                        <p className="font-semibold text-muted-foreground">Partidas fuera de umbral ({a.offenders.length}):</p>
                                        {a.offenders.slice(0, 5).map((o, i) => (
                                          <div key={i} className="flex items-center justify-between gap-2 pl-2">
                                            <span className="truncate">{o.category}</span>
                                            <span className={`font-mono font-semibold ${Math.abs(o.dev_pct) >= 50 ? "text-red-600" : "text-amber-600"}`}>
                                              {o.dev_pct > 0 ? "+" : ""}{o.dev_pct}%
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {a.ok && a.kind === "budget_forecast" && (
                                  <div className="mt-2 space-y-1.5">
                                    <div className="flex flex-wrap gap-2 text-[11px]">
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">Real YTD {(a.real_ytd_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">Forecast Dic {(a.forecast_year_end_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">Plan Dic {(a.plan_year_end_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                      <span className={`px-1.5 py-0.5 rounded font-mono font-bold ${Math.abs(a.gap_year_end_pct) >= 20 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                                        Δ cierre {a.gap_year_end_pct > 0 ? "+" : ""}{a.gap_year_end_pct}%
                                      </span>
                                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]">
                                        {a.months_real}/12 meses · método {a.method}
                                      </span>
                                    </div>
                                    {a.commentary_ai && (
                                      <div className="text-[11px] leading-relaxed p-2 bg-white/70 rounded border-l-2 border-blue-500">
                                        {a.commentary_ai}
                                      </div>
                                    )}
                                    {a.critical_categories?.length > 0 && (
                                      <div className="text-[11px] space-y-0.5 mt-1">
                                        <p className="font-semibold text-muted-foreground">Categorías críticas ({a.critical_categories.length}):</p>
                                        {a.critical_categories.slice(0, 5).map((c, i) => (
                                          <div key={i} className="flex items-center justify-between gap-2 pl-2">
                                            <span className="truncate">{c.category}</span>
                                            <span className={`font-mono font-semibold ${Math.abs(c.gap_pct) >= 50 ? "text-red-600" : "text-amber-600"}`}>
                                              {c.gap_pct > 0 ? "+" : ""}{c.gap_pct}%
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {a.ok && a.kind === "budget_rebalance" && (
                                  <div className="mt-2 space-y-1.5">
                                    <div className="flex flex-wrap gap-2 text-[11px]">
                                      <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-mono font-bold">
                                        {a.proposals?.length || 0} propuestas · {(a.total_moved_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })} a mover
                                      </span>
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">
                                        Value 360 {(a.value_360_total_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
                                      </span>
                                    </div>
                                    {a.commentary_ai && (
                                      <div className="text-[11px] leading-relaxed p-2 bg-white/70 rounded border-l-2 border-violet-500">
                                        {a.commentary_ai}
                                      </div>
                                    )}
                                    {a.proposals?.length > 0 && (
                                      <div className="space-y-1.5 mt-1">
                                        {a.proposals.slice(0, 5).map((p, i) => (
                                          <div key={i} className="text-[11px] p-2 rounded border border-violet-200 bg-violet-50/60">
                                            <div className="flex items-center gap-1.5 font-semibold text-violet-900 mb-0.5">
                                              <span className="truncate">{p.from_category}</span>
                                              <span className="text-violet-500">→</span>
                                              <span className="truncate">{p.to_lever}</span>
                                              <span className="ml-auto font-mono">{(p.amount_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
                                              {p.expected_roi_multiple && (
                                                <span className="px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">×{p.expected_roi_multiple}</span>
                                              )}
                                            </div>
                                            <p className="text-muted-foreground leading-snug">{p.rationale}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {a.ok && a.kind === "budget_rebalance_committed" && (
                                  <div className="mt-2 space-y-1.5">
                                    <div className="flex flex-wrap gap-2 text-[11px]">
                                      <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono font-bold">
                                        ✅ {a.applied_count} aplicadas · {(a.total_moved_eur || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
                                      </span>
                                      <span className="px-1.5 py-0.5 rounded bg-white border font-mono">+{a.milestones_added} hitos al plan</span>
                                      {a.teams_sent && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px]">Teams notificado ✓</span>}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground italic">Trazabilidad completa en <b>impacto_config.budget_adjustments</b> y colección <b>rebalance_log</b>.</p>
                                  </div>
                                )}
                                {a.ok && a.link && (
                                  <button
                                    onClick={() => { setOpen(false); navigate(a.link); }}
                                    className="text-[11px] text-emerald-600 font-bold mt-1 flex items-center gap-0.5 hover:underline"
                                    data-testid={`copilot-action-link-${ai}`}
                                  >
                                    Ver resultado <ArrowRight className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-3.5 h-3.5" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[9px] font-black animate-pulse"
                      style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}>
                      VAL
                    </div>
                    <div className="bg-muted rounded-xl px-3.5 py-2.5 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                      <span className="text-xs text-muted-foreground">Analizando tus datos...</span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="p-3 border-t flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pregunta a VAL..."
                className="resize-none min-h-[44px]"
                rows={2}
                data-testid="copilot-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="self-end"
                style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}
                data-testid="copilot-send"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MemoryAdder({ onAdd, placeholder, testIdPrefix = "memory" }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try { await onAdd(text.trim()); setText(""); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder || 'Ej: "Prefiero borradores técnicos con TRL"'}
        className="flex-1 text-sm px-3 py-2 rounded-lg border bg-card focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`${testIdPrefix}-input`}
      />
      <button
        type="submit"
        disabled={saving || !text.trim()}
        className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-add-btn`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
      </button>
    </form>
  );
}
